import rg4js from 'raygun4js';
import React, { createContext, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { useGet } from 'utils/hooks/useAPI';

export interface DeploymentContext {
    is_production: boolean;
    display_label: string | null;
}

export interface MetadataContextType {
    version: string;
    schema_version: number;
    auth0_enabled: boolean;
    env: 'dev' | 'retail' | null;
    deployment?: DeploymentContext;
}

const METADATA_API = '/helios-api/metadata/';

// Re not-null assertion: See null check in useSiteContext hook
export const MetadataContext = createContext<MetadataContextType>(null!);

export const MetadataProvider = ({ children }: PropsWithChildren) => {
    const [{ data, error }] = useGet<MetadataContextType>(METADATA_API);

    const metadata = useMemo(() => {
        if (data) {
            return data;
        }
        if (error) {
            rg4js('send', {
                error,
                customData: [
                    {
                        description:
                            'Unable to load metadata, providing fallback',
                    },
                ],
            });

            const fallback: MetadataContextType = {
                version: 'unknown',
                schema_version: 0,
                auth0_enabled: true,
                env: null,
                deployment: undefined,
            };
            return fallback;
        }
    }, [data, error]);

    if (!metadata) {
        return <></>;
    }

    return <MetadataContext value={metadata}>{children}</MetadataContext>;
};

export const useMetadataContext = () => {
    const metadataContext = React.useContext(MetadataContext);

    if (!metadataContext) {
        throw new Error(
            'useMetadataContext must be used within a MetadataProvider'
        );
    }

    return metadataContext;
};
